import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as dayjs from 'dayjs';

import { subscriptionDetails, subscriptionUpdate } from '../../services/subscriptions';
import { teamsList } from '../../services/teams';
import Switch from '../../components/shared/switch';

const EditSubscription = () => {
  const [subscription, setSubscription] = useState({});
  const [teams, setTeams] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [showSwitchAlert, setShowSwitchAlert] = useState(false);

  let { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navigateBack = () => {navigate("/club_admin/subscriptions/")};

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      await subscriptionUpdate(subscription);
      navigateBack();
    } catch (e) {
      if (e?.response?.data?.errors) setErrors(e?.response?.data?.errors);
    }
    setLoading(false);
  };

  useEffect(() => {
    const getMyTeams = async () => {
      const { teams } = await teamsList();
      setTeams(teams);
    };

    getMyTeams();
  }, []);

  useEffect(() => {
    const getDetails = async (id) => {
      const { subscription } = await subscriptionDetails(id);
      setSubscription(subscription);
    };

    if (id) getDetails(id);
  }, [id]);

  useEffect(() => {
    if (subscription?.team_ids?.length >= subscription?.subscription_type?.allowed_teams) {
      setShowLimitAlert(true);
    } else {
      setShowLimitAlert(false);
    }

    if (subscription?.switch_forbidden) {
      setShowSwitchAlert(true);
    }
  }, [subscription]);

  const handleToggle = (teamId) => {
    const isAlreadySelected = subscription?.team_ids.includes(teamId);

    if (subscription?.team_ids?.length >= subscription?.subscription_type?.allowed_teams && !isAlreadySelected) {
      setShowLimitAlert(true); 
      return; 
    }

    const updatedSubscription = { ...subscription };
    if (isAlreadySelected) {
      updatedSubscription.team_ids = updatedSubscription?.team_ids.filter(id => id !== teamId);
    } else {
      updatedSubscription.team_ids.push(teamId);
    }

    setSubscription(updatedSubscription);
  };

  return (
    <div className="form-container user-form">
      {showLimitAlert && (
        <div className="alert">
          <h4>{t('subscriptions.reach_limits', { count: subscription?.subscription_type?.allowed_teams })}</h4>
          <div>
            <a href={`mailto:Hello@supercoachapp.com?subject=${`Buy more licenses`}`} className="btn btn-primary">
             {t('subscriptions.buy_more')}
            </a>
          </div>
        </div>
      )}

      {showSwitchAlert && (
        <div className="alert">
          <h4>{t('subscriptions.switch_limits', { count: subscription?.subscription_type?.allowed_teams })}</h4>
        </div>
      )}

      <div className="form-container-header">
        <div className="form-container-header-text">{subscription?.premium?.title}</div>
      </div>
      {subscription?.id && (
        <div className="form-container-info">
          <div className="form-container-info-text"></div>
          <div className="form-container-info-text-secondary flex-end">
            {t('subscriptions.expires')}: {dayjs(subscription?.expires_at).format('YYYY-MM-DD')}
          </div>
        </div>
      )}
      <h4>{t('subscriptions.note')}</h4>
      <div>
        <form onSubmit={submit}>
          <div className="premium-teams">
            <div className="premium-teams-column left">
              {teams.slice(0, Math.ceil(teams.length / 2)).map((team) => (
                <div key={team.id} className="premium-teams-item">
                  <Switch
                    value={subscription?.team_ids?.includes(team.id)}
                    onChange={() => handleToggle(team.id)}
                    disabled={subscription?.team_ids?.length >= subscription?.subscription_type?.allowed_teams && !subscription?.team_ids?.includes(team.id)}
                  />
                  <div className="subscription_name">{team.name}</div>
                </div>
              ))}
            </div>
            <div className="premium-teams-column right">
              {teams.slice(Math.ceil(teams.length / 2)).map((team) => (
                <div key={team.id} className="premium-teams-item">
                  <Switch
                    value={subscription?.team_ids?.includes(team.id)}
                    onChange={() => handleToggle(team.id)}
                    disabled={subscription?.team_ids?.length >= subscription?.subscription_type?.allowed_teams && !subscription?.team_ids?.includes(team.id)}
                  />
                  <div className="subscription_name">{team.name}</div>
                </div>
              ))}
            </div>
          </div>
        </form>
      </div>
      <div className="form-container-actions">
        <div className="flex-end">
          <button className="button" onClick={navigateBack} disabled={loading}>
            {t('buttons.common.cancel')}
          </button>
          <button type="submit" className="button button-primary" onClick={submit} disabled={loading}>
            {t('buttons.common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(EditSubscription);